import { lazy, Suspense, useCallback } from 'react';
import type { MenuItemSubMenuViewModel } from 'autogen/swagger/all';
import { useMenuQuery } from 'autogen/swagger/all/hooks';
import useDrawer from 'components/generic/Drawer/hooks/useDrawer';
import Icon from 'components/Icon';
import useIsPathnameActive from 'components/sideMenu/shared/hooks/useIsPathnameActive';
import requestContext from 'modules/requestContext';
import queryStaleTimes from 'setup/react-query/queryStaleTimes';
import { useSideMenu } from '../../../../shared/context/SideMenuProvider';
import type { DefaultMenuItemProps } from '../types';
import DefaultMenuItem from './DefaultMenuItem';

const MenuWithHeader = lazy(() => import('components/sideMenu/components/Menu/components/MenuWithHeader'));

const context = requestContext;

type SubMenuItemProps = {
    model: MenuItemSubMenuViewModel;
    defaultProps: DefaultMenuItemProps;
};

export default function SubMenuItem({
    /*
        subMenuInitialData: the initial data that came from the parent menu to display the items that have subMenus,
        The initial data doesn't include sections, We fetch that here in this component if we have reached the shouldPrefetch depth.
    */
    model: { subMenu: subMenuInitialData, text, productGroupPath },
    /* default props */
    defaultProps: { iconUrl, styling },
    ...restProps
}: SubMenuItemProps) {
    const drawerId = `submenu-${subMenuInitialData.id}`;
    const { open: openSubMenu, close: closeSubMenu } = useDrawer(drawerId, { animate: false });
    const { currentPrefetchedDepth, rootMenuPrefetchDepth, subMenuPrefetchDepth } = useSideMenu();
    const isSelected = useIsPathnameActive(productGroupPath, subMenuInitialData?.level - rootMenuPrefetchDepth);

    const {
        data: subMenu,
        refetch: prefetchMenu,
        isStale
    } = useMenuQuery(
        {
            menuId: subMenuInitialData.id,
            depth: subMenuPrefetchDepth,
            display: context.posRequest?.isPosRequest ? 'Pos' : 'Webshop'
        },
        {
            enabled: false,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            staleTime: queryStaleTimes.menu
        }
    );

    const prefetch = useCallback(() => {
        // start prefetching when the current lvl is equal to or higher than the current prefetched depth
        const shouldPrefetch = subMenuInitialData.level >= currentPrefetchedDepth.current;
        if (shouldPrefetch && isStale) {
            prefetchMenu().then(() => {
                // increment the current depth by subMenuPrefetchDepth's value
                currentPrefetchedDepth.current += subMenuPrefetchDepth;
            });
        }
    }, [prefetchMenu, currentPrefetchedDepth, subMenuInitialData.level, subMenuPrefetchDepth, isStale]);

    const resetCurrentPrefetchDepth = useCallback(() => {
        // reset prefetchDepth only to a minimum of the RootMenu prefetch depth
        if (currentPrefetchedDepth.current > rootMenuPrefetchDepth) {
            currentPrefetchedDepth.current = subMenuInitialData.level;
        }
    }, [currentPrefetchedDepth, rootMenuPrefetchDepth, subMenuInitialData.level]);

    return (
        <>
            <DefaultMenuItem.Button
                buttonProps={{
                    onClick: () => {
                        openSubMenu();
                        prefetch();
                    }
                }}
                AfterRenderer={() => <Icon type="ChevronRight" size={16} />}
                iconUrl={iconUrl}
                styling={styling}
                selected={isSelected}
                {...restProps}
            >
                {text}
            </DefaultMenuItem.Button>

            {/* THIS ITEM MOUNTS THROUGH A REACT PORTAL - is inserted into the sideDrawer */}
            <Suspense>
                <MenuWithHeader
                    drawerId={drawerId}
                    menu={subMenu || subMenuInitialData}
                    backButtonCallback={() => {
                        closeSubMenu();
                        resetCurrentPrefetchDepth();
                    }}
                    animate={false}
                />
            </Suspense>
        </>
    );
}
